<template>
  <div
    class="product-card-container relative"
    :class="{'popular-card-container': product.isPopular}"
  >
    <div class="space-y-3">
      <div class="flex items-center justify-between text-text-muted">
        <BaseText
          type="label"
        >
          {{ product.tier }}
        </BaseText>
        <div
          v-if="product.isPopular"
          class="flex items-center gap-1"
        >
          <FlameIcon class="text-icon-muted" />
          <BaseText
            type="label"
            size="sm"
          >
            Popular
          </BaseText>
        </div>
      </div>
      <div class="flex items-center gap-1">
        <BaseText
          class="text-text-muted"
          type="display"
          size="xs"
        >
          ${{ finalPrice }}
        </BaseText>
        <BaseText
          v-if="isSaleActive && product.key !== 'basic-fp-plan'"
          type="display"
          size="xs"
          class="text-text-subdued line-through"
        >
          ${{ product.price?.monthly }}
        </BaseText>
        <BaseText
          size="sm"
          class="text-text-subdued pt-2"
        >
          /month <span v-if="!isMonthly">billed annually</span>
        </BaseText>
      </div>
      <!-- <BaseHeightTransition mode="out-in"> -->
      <div
        v-if="isSaleActive || !isMonthly"
        class="transition-height"
        :style="{ height: isSaleActive ? '60px' : '20px' }"
      >
        <div
          v-if="isSaleActive"
          key="monthly-discount"
          class="fade-in"
        >
          <div
            v-if="product.key === 'basic-fp-plan'"
            class="w-full rounded-lg p-2 bg-background-normal"
          >
            <BaseText
              size="sm"
              class="text-text-normal basic-nonsale-banner"
            >
              Upgrade to Workflow or Agency for the 50% discount off first month.
            </BaseText>
          </div>
          <div
            v-else
            class="w-full rounded-lg p-2 space-y-1 text-secondary-green-300"
            style="background-color:#effefa"
          >
            <div
              class="flex items-center gap-1 "
            >
              <CouponIcon class="w-5 h-5" />
              <BaseText
                type="label"
                size="sm"
              >
                50% off first month.
              </BaseText>
            </div>
            <BaseText size="sm">
              ${{ product.price?.[intervalType] }}/mo after first month.
            </BaseText>
          </div>
        </div>
        <div
          v-else
          key="save-annual"
          class="flex items-center gap-1 fade-in"
        >
          <MoneyCircleIcon class="money-icon" />
          <BaseText
            type="label"
            size="sm"
            class="text-text-muted"
          >
            Save ${{ product.annualSavings }} annually
          </BaseText>
        </div>
      </div>
      <button
        class="flex items-center justify-center"
        :class="product.isPopular ? 'black-btn-primary text-white w-full px-2.5 py-2 rounded-md' : 'base-upgrade-btn'"
        :disabled="loadingId !== null || (activePlan === product.key && activeInterval === intervalType) || notAdmin"
        @click="handleCheckout"
      >
        <BaseLoadingSpinnerCircle
          v-if="loadingId === product.key"
          class="text-text-muted"
        />
        <BaseText
          v-else
          type="label"
          size="sm"
        >
          {{ upgradeCTA }}
        </BaseText>
      </button>
    </div>
    <div class="mt-5">
      <BaseText
        type="label"
        size="sm"
        class="text-text-muted"
      >
        {{ product.baseUserCount }} User{{ product.baseUserCount > 1 ? 's' : '' }} included
      </BaseText>
      <BaseText
        size="sm"
        class="text-text-subdued"
      >
        $20 per additional user
      </BaseText>
    </div>
    <div
      v-for="includedProduct in allProducts"
      :key="includedProduct.key"
      class="flex items-center w-full gap-3 mt-3"
      :class="{'not-included-product' : !isProductIncluded(includedProduct.key)}"
    >
      <img
        class="w-7 h-7"
        :src="includedProduct.imgUrl"
      >
      <BaseText class="flex-1">
        {{ includedProduct.label }}
      </BaseText>
      <div v-if="isProductIncluded(includedProduct.key)">
        <BaseText
          v-if="includedProduct.key === 'spyder'"
          size="sm"
          class="text-text-normal"
        >
          <span v-if="isMonthly">
            {{ product.baseSpyder }} Brand{{ product.baseSpyder > 1 ? 's' : '' }}
          </span>
          <span v-else>Unlimited</span>
        </BaseText>
        <BaseText
          v-else-if="includedProduct.key === 'lens'"
          size="sm"
          class="text-text-normal"
        >
          {{ product.baseLens }} Ad Account{{ product.baseLens > 1 ? 's' : '' }}
        </BaseText>
        <CheckmarkIcon
          v-else
          class="text-text-muted"
        />
      </div>
      <TagRemoveIcon v-else />
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
// Icons
import CheckmarkIcon from '../../globals/Icons/CheckmarkIcon.vue'
import TagRemoveIcon from '../../globals/Icons/TagRemoveIcon.vue'
import FlameIcon from '../../globals/Icons/FlameIcon.vue'
import CouponIcon from '../../globals/Icons/CouponIcon.vue'
import MoneyCircleIcon from '../../globals/Icons/MoneyCircleIcon.vue'
export default {
  name: 'ProductCard',
  components: {
    CheckmarkIcon,
    TagRemoveIcon,
    MoneyCircleIcon,
    CouponIcon,
    FlameIcon
  },
  props: {
    product: {
      type: Object,
      default: () => {}
    },
    isMonthly: {
      type: Boolean,
      default: false
    },
    allProducts: {
      type: Array,
      default: () => []
    },
    showDiscount: {
      type: Boolean,
      default: () => true
    },
    loadingId: {
      type: [String, null],
      default: null
    },
    activePlan: {
      type: [String, null],
      default: null
    },
    activeInterval: {
      type: [String, null],
      default: null
    },
    isLegacyUser: {
      type: Boolean,
      default: () => false
    }

  },
  data () {
    return {

    }
  },
  computed: {
    ...mapGetters('AuthModule', ['getStripeCustomer', 'isTeamOwner', 'getTeam']),
    notAdmin () {
      return this.getTeam && !this.isTeamOwner
    },
    upgradeCTA () {
      if (this.getTeam && !this.isTeamOwner) {
        return 'Admin only'
      }

      return (this.activePlan === this.product.key && this.activeInterval === this.intervalType)
        ? 'Current Plan'
        : this.isSubscriptionInactive
          ? 'Upgrade Now'
          : 'Change plan'
    },
    isSubscriptionInactive () {
      return !this.activePlan
    },
    finalPrice () {
      const prodPrice = this.product.price?.[this.intervalType]
      return this.isSaleActive && this.product.key !== 'basic-fp-plan' ? (prodPrice / 2).toFixed(0) : prodPrice
    },
    isSaleActive () {
      return this.isMonthly && this.showDiscount
    },
    intervalType () {
      return this.isMonthly ? 'monthly' : 'annual'
    }
  },
  methods: {
    isProductIncluded (key) {
      return this.product.includedProducts.includes(key)
    },
    handleCheckout () {
      if (this.isSubscriptionInactive && !this.isLegacyUser) {
        const product = this.product
        const intervalType = this.intervalType

        const lensCart = product?.addOns?.lens?.[intervalType]
        const spyderCart = product?.addOns?.spyder?.[intervalType]
        const sessionPayload = {
          key: product.key,
          priceId: product.priceId?.[intervalType],
          addOn: {
            ...(lensCart && { lens: lensCart }),
            ...(spyderCart && { spyder: spyderCart })
          }
        }
        this.$emit('createSession', sessionPayload)
      } else {
        this.$emit('changePlan', { ...this.product, interval: this.intervalType })
      }
    }
  }
}
</script>

<style scoped>
.product-card-container{
  padding: 16px;
  border-radius: 10px;
  border: 1px solid #DFE1E7;
  width: 288px;
  transition: height 150ms ease-in-out;
}
.popular-card-container{
  border: 1px solid rgba(1, 2, 9, 0.98)
}
.popular-banner{
  color: white;
  background-color:rgba(1, 2, 9, 0.98);
  border-radius: 4px;
  position: absolute;
  left: 0;
  right:0;
  margin-left:auto;
  margin-right:auto;
  top: 0;
  transform: translateY(-50%);
}

.base-upgrade-btn{
  padding: 8px 10px;
  transition: box-shadow 100ms ease-in-out, background-color 100ms ease-in-out;
  width: 100%;
  border-radius: 6px;
  color: #303546;
  box-shadow: 0px 1px 2px 0px rgba(4, 26, 75, 0.13), 0px 0px 0px 1px rgba(0, 56, 108, 0.08);
}

.base-upgrade-btn:hover {
  box-shadow: 0px 1px 2px 0px rgba(4, 26, 75, 0.17), 0px 0px 0px 1px rgba(0, 56, 108, 0.12);
}

.base-upgrade-btn:disabled {
    color: #a4abb8 !important;
    background-color: #F6F8FA !important;
    box-shadow: none;
    cursor: not-allowed
}

.not-included-product{
  filter: grayscale(100%);
  opacity: 0.4;
}

.basic-nonsale-banner{
  display: -webkit-box;
  -webkit-line-clamp: 2;   /* number of lines to show */
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.fade-in {
  animation: fadeIn 100ms ease-in;
}
@keyframes fadeIn {
  from { opacity: 0.25 }
  to { opacity: 1 }
}

.money-icon{
  color: #EBBE7A
}

</style>
